import React, { useState } from 'react';
import { Bell, ArrowLeft, RefreshCw } from 'lucide-react';
import { getProductImage } from '../utils/imageHelpers';
import ProductDetailsModal from './modals/ProductDetailsModal';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(date.getDate()).padStart(2,'0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const SubscriptionsScreen = ({
  allSubscriptions = [],
  loading = false,
  loadSubscriptions,
  handlePauseSubscription,
  handleResumeSubscription,
  handleCancelSubscription,
  handleModifySubscription,
  handleReactivateSubscription,
  appTheme = 'blue',
  setActiveTab,
  goBack,
  isDesktop = false,
  addToCart,
  showToast,
  toggleFavorite,
  favorites = [],
  setShowSubscription,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const openDetails = (prod) => {
    if (!prod || (!prod.id && !prod.name)) return;
    setSelectedProduct(prod);
    setShowProductDetails(true);
  };

  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };

  const renderCard = (sub) => {
    const statusLower = (sub.status || '').toLowerCase();
    const prod        = sub.product || {};
    const mrp         = parseFloat(prod.price        || sub.price        || 0);
    const salePrice   = parseFloat(prod.sale_price   || sub.salePrice    || mrp);
    const subPrice    = parseFloat(prod.subscription_price || sub.subscriptionPrice || salePrice);
    const discount    = sub.plan?.discount || sub.planDiscount || sub.discount || 0;
    const perDelivery = subPrice > 0 ? subPrice * (1 - discount / 100) : 0;
    const freq        = sub.planId || sub.frequency || '';
    const deliveries  = freq === 'daily' ? 30 : freq === 'alternate' ? 15 : 4;
    const totalSaving = mrp > 0 ? (mrp * deliveries) - (perDelivery * deliveries) : 0;

    const statusColors = {
      active:    'bg-green-100 text-green-700',
      paused:    'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-600',
      canceled:  'bg-red-100 text-red-600',
      expired:   'bg-gray-100 text-gray-500',
    };
    const statusCls = statusColors[statusLower] || 'bg-gray-100 text-gray-500';

    return (
      <div key={sub.id || sub.subscription_number} className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Card header — same gradient style as OrdersScreen */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 border-b border-green-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-gray-800">
                {sub.subscription_number || (sub.id ? `Subscription #${sub.id}` : 'Subscription')}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                {sub.plan?.name || sub.planName || sub.frequency || '—'} plan
              </p>
              <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                📅 Started: {fmtDate(sub.startDate || sub.created_at)}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusCls}`}>
              {statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}
            </span>
          </div>
        </div>

        {/* Card body */}
        <div className="p-4">
          {/* Product row */}
          <div
            className="flex items-center gap-3 mb-3 cursor-pointer"
            onClick={() => openDetails(prod)}
            title="View details"
          >
            <div className="flex-shrink-0">{getProductImage(prod, 'large')}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 truncate hover:text-orange-500">
                {prod.name || sub.productName || 'Subscription'}
              </p>
              {prod.description && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{prod.description}</p>
              )}
              {sub.deliverySlot && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {sub.deliverySlot === 'morning' ? '🌅 Morning (6–8 AM)' : sub.deliverySlot}
                </p>
              )}
            </div>
          </div>

          {/* Pricing table — mirrors order totals */}
          {mrp > 0 && (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 mb-3 text-sm">
              <div className="flex justify-between px-3 py-2">
                <span className="text-gray-500">MRP / delivery</span>
                <span className="text-gray-400 line-through">₹{mrp.toFixed(2)}</span>
              </div>
              {salePrice < mrp && (
                <div className="flex justify-between px-3 py-2">
                  <span className="text-gray-500">Sale price / delivery</span>
                  <span className="text-gray-600">₹{salePrice.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between px-3 py-2">
                <span className="text-gray-500">Subscription price</span>
                <span className="font-semibold text-blue-600">
                  ₹{(perDelivery > 0 ? perDelivery : salePrice).toFixed(2)} / delivery
                </span>
              </div>
              {totalSaving > 0 && (
                <div className="flex justify-between px-3 py-2 bg-green-50 rounded-b-lg">
                  <span className="text-green-700 font-medium">
                    Total saving ({deliveries} deliveries)
                  </span>
                  <span className="font-bold text-green-600">₹{totalSaving.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons — mirrors order actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {statusLower === 'cancelled' || statusLower === 'canceled' ? (
              <>
                <button
                  onClick={() => handleReactivateSubscription && handleReactivateSubscription(sub.id)}
                  className="flex-1 text-blue-600 text-sm font-medium bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 transition-all"
                >
                  Reactivate
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-gray-600 text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            ) : statusLower === 'paused' || statusLower === 'inactive' ? (
              <>
                <button
                  onClick={() => handleResumeSubscription && handleResumeSubscription(sub.id)}
                  className="flex-1 text-green-600 text-sm font-medium bg-green-50 px-3 py-2 rounded-lg hover:bg-green-100 transition-all"
                >
                  Resume
                </button>
                <button
                  onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)}
                  className="flex-1 text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-gray-600 text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handlePauseSubscription && handlePauseSubscription(sub.id)}
                  className="flex-1 text-orange-600 text-sm font-medium bg-orange-50 px-3 py-2 rounded-lg hover:bg-orange-100 transition-all"
                >
                  Pause
                </button>
                <button
                  onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)}
                  className="flex-1 text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)}
                  className="flex-1 text-gray-600 text-sm font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  Modify Plan
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pb-20 min-h-screen bg-gray-50">
        <div className={`p-6 ${isDesktop ? 'rounded-3xl' : 'rounded-b-3xl'}`} style={headerStyle}>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => goBack ? goBack() : setActiveTab('profile')}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
              style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
            >
              <ArrowLeft size={22} />
            </button>
          </div>
          <h1 className="text-2xl font-bold" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>
            My Subscriptions
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center mt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-500">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`p-6 ${isDesktop ? 'rounded-3xl' : 'rounded-b-3xl'}`} style={headerStyle}>
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => goBack ? goBack() : setActiveTab('profile')}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
          {loadSubscriptions && (
            <button
              onClick={loadSubscriptions}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
              style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
          )}
        </div>
        <h1 className="text-2xl font-bold" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>
          My Subscriptions
        </h1>
        <p style={appTheme === 'white' ? { color: '#888' } : { color: 'var(--accent-light)' }}>
          Manage your recurring deliveries
        </p>
      </div>

      {/* Content */}
      <div className="px-4 pt-4 pb-4">
        {allSubscriptions.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-16 px-6 text-center">
            <Bell size={64} className="text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No subscriptions yet</h3>
            <p className="text-gray-400 mb-6">Subscribe to your favourite products for guaranteed daily deliveries.</p>
            <button
              onClick={() => setActiveTab('home')}
              className="px-6 py-2.5 rounded-xl bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-all"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className={isDesktop ? 'grid grid-cols-1 lg:grid-cols-2 gap-5 items-start' : 'space-y-4'}>
            {allSubscriptions.map((sub, idx) => (
              <div key={sub.id || sub.subscription_number || idx}>
                {renderCard(sub)}
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductDetailsModal
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default SubscriptionsScreen;
